<template>
    <div>
        <b-modal id="modal-delete" title="Delete">
            <p>Are you sure?</p>
            <ul>
                <li>Deleting this rule will halt Jira sync for the associated status.</li>
                <li>Posts within ProductLift will remain unaffected, retaining their current status and Jira linkage (key/id).</li>
            </ul>
            <template #modal-footer="{ ok, cancel, hide }">
                <b-button variant="outline" @click="cancel()">Cancel</b-button>
                <b-button variant="danger" @click="deleteJiraStatusMapping(deleteId)">Delete</b-button>
            </template>
        </b-modal>


        <div v-if="loading">Loading...</div>
        <div v-else>
            <div class="list-group">
                <template v-for="mapping in mappings">
                    <div class="list-group-item bg-white">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                When a Jira issue is changed to &quot;<b>{{mapping.jira_status}}</b>&quot;, move all linked ProductLift posts to the
                                <status-badge :status="mapping.status" size="sm" /> status
                                <span v-if="mapping.auto_notify_voters"> and <b>notify</b> followers.</span>
                                <span v-else> and <b>don't notify</b> followers.</span>
                            </div>
                            <div class="ml-2 text-right" style="width: 150px;">
                                <b-button variant="outline" class="btn-sm" @click="openEdit(mapping.id)"><b-icon icon="pencil-square"></b-icon></b-button>
                                <b-button variant="outline" @click="askDelete(mapping.id)" class="btn-sm"><b-icon icon="trash"></b-icon></b-button>
                            </div>
                        </div>

                        <div v-if="editId === mapping.id">
                            <hr/>
                            <validation-observer ref="observer" v-slot="{ passes }">
                                <form @submit.stop.prevent="passes(editJiraStatusMapping)">
                                    <div class="form-group">
                                        <validation-provider name="jira_status" rules="required" v-slot="{ errors }">
                                            <label for="jiraStatus">Jira Status</label>
                                            <b-form-input id="jiraStatus" v-model="jiraStatus" placeholder="Done"></b-form-input>
                                            <span class="text-danger">{{ errors[0] }}</span>
                                        </validation-provider>
                                    </div>

                                    <div class="form-group">
                                        <validation-provider name="status_id" rules="required" v-slot="{ errors }">
                                            <label for="statusId">ProductLift Status</label>
                                            <b-form-select id="statusId" v-model="statusId" :options="statuses"></b-form-select>
                                            <span class="text-danger">{{ errors[0] }}</span>
                                        </validation-provider>
                                    </div>

                                    <div class="form-group">
                                        <b-form-checkbox v-model="autoNotifyVoters">
                                            Auto Notify Voters
                                        </b-form-checkbox>
                                    </div>

                                    <div class="col-4">
                                        <a class="btn btn-outline mt-2 mr-2" @click='editId = 0'>Cancel</a>
                                        <button type="submit" class="btn btn-success  mt-2">Save</button>
                                    </div>
                                </form>
                            </validation-observer>
                        </div>
                    </div>
                </template>
            </div>

            <b-button class="mt-4 btn-outline" @click='isNewFormOpen = true'>
                <b-icon-plus class="text-success"></b-icon-plus>
                New Jira Rule
            </b-button>

            <div v-if="isNewFormOpen">
                <hr/>
                <validation-observer ref="observer" v-slot="{ passes }">
                    <form @submit.stop.prevent="passes(createJiraStatusMapping)">
                        <div class="form-group">
                            <validation-provider name="jira_status" rules="required" v-slot="{ errors }">
                                <label for="jiraStatus">Jira Status</label>
                                <b-form-input id="jiraStatus" v-model="jiraStatus" placeholder="Done"></b-form-input>
                                <span class="text-danger">{{ errors[0] }}</span>
                            </validation-provider>
                        </div>

                        <div class="form-group">
                            <validation-provider name="status_id" rules="required" v-slot="{ errors }">
                                <label for="statusId">ProductLift Status</label>
                                <b-form-select id="statusId" v-model="statusId" :options="statuses"></b-form-select>
                                <span class="text-danger">{{ errors[0] }}</span>
                            </validation-provider>
                        </div>

                        <div class="form-group">
                            <b-form-checkbox v-model="autoNotifyVoters">
                                Auto Notify Voters
                            </b-form-checkbox>
                        </div>

                        <a class="btn btn-outline-secondary mt-2 mr-2" @click='isNewFormOpen = false'>Cancel</a>
                        <button type="submit" class="btn btn-success mt-2">Create</button>
                    </form>
                </validation-observer>

            </div>
        </div>
    </div>
</template>

<script>
import StatusBadge
    from "./StatusBadge";
import NotificationPop
    from "../mixins/NotificationPop";

export default {
    name: "JiraStatusMappings",
    mixins: [NotificationPop],
    components: {
        StatusBadge,
    },
    data() {
        return {
            loading: true,
            mappings: [],
            statuses: [],
            isNewFormOpen: false,
            editId: null,
            deleteId: null,
            jiraStatus: '',
            statusId: null,
            autoNotifyVoters: false,
        }
    },

    created() {
        this.$http.get('/http_api/jira_status_mappings')
            .then(({data}) => {
                this.loading = false;
                this.mappings = data.data
                console.log(data.data)
            })
            .catch(error => {
                this.loading = false;
                this.popNotification('Something went wrong on our side. Could not load. Please try again or contact ruben@productlift.dev', 'danger')
            });

        this.$http.get('/http_api/statuses')
            .then(({data}) => {
                // Transform statuses into a format suitable for b-form-select
                this.statuses = data.data.map(status => ({
                    text: status.name,
                    value: status.id
                }));
                console.log(this.statuses);
            })
            .catch(error => {
                this.loading = false;
                this.popNotification('Something went wrong on our side. Could not load. Please try again or contact ruben@productlift.dev', 'danger');
            });

    },

    methods: {
        /**
         * Create new JiraStatusMapping
         */
        createJiraStatusMapping: function () {
            this.$http.post('/http_api/jira_status_mappings',{
                jira_status: this.jiraStatus,
                status_id: this.statusId,
                auto_notify_voters: this.autoNotifyVoters,
            })
                .then(({data}) => {
                    // add to list
                    this.mappings.push(data.data);
                    // clear form and toggle
                    this.jira_status = ''
                    this.status_id = null
                    this.isNewFormOpen = false
                    this.popNotification('Created', 'success')
                })
                .catch(error => {
                    // handle authentication and validation errors here
                    console.log(error.response.data.errors)
                    this.popNotification('Could not create', 'danger')
                })
        },

        /**
         * Display edit form inside item
         * @param id JiraStatusMapping id
         */
        openEdit: function (id) {
            let index = this.mappings.findIndex(mapping => mapping.id === id)

            this.editId = id
            this.jiraStatus = this.mappings[index].jira_status
            this.statusId = this.mappings[index].status_id
            this.autoNotifyVoters = this.mappings[index].auto_notify_voters
        },

        /**
         * Save edit form
         */
        editJiraStatusMapping: function () {
            let index = this.mappings.findIndex(mapping => mapping.id === this.editId)

            this.$http.patch('/http_api/jira_status_mappings/' + this.editId,{
                jira_status: this.jiraStatus,
                status_id: this.statusId,
                auto_notify_voters: this.autoNotifyVoters,
            })
                .then(({data}) => {
                    this.popNotification('Updated', 'success')
                    this.mappings[index] = data.data
                    console.log(data.data)

                    this.editId = 0 // close form
                    this.jira_status = ''
                    this.status_id = null
                })
                .catch(error => {
                    // handle authentication and validation errors here
                    console.log(error.response.data.errors)
                    this.popNotification('Could not update', 'danger')
                })
        },

        /**
         * Show modal to ask for deletion
         * @param id
         */
        askDelete: function (id) {
            this.$bvModal.show('modal-delete')
            this.deleteId = id
        },

        /**
         * After confirm, delete the JiraStatusMapping
         * @param id JiraStatusMapping id
         */
        deleteJiraStatusMapping: function (id) {
            // Close the modal
            this.$bvModal.hide('modal-delete')

            let index = this.mappings.findIndex(mapping => mapping.id === id)

            this.$http.delete('/http_api/jira_status_mappings/' + id)
                .then(({response}) => {
                    this.popNotification('Deleted', 'success')
                })
                .catch(error => {
                    // handle authentication and validation errors here
                    console.log(error.response.data.errors)
                    this.popNotification('Could not delete', 'danger')
                })

            this.mappings.splice(index, 1)
        },
    }
}
</script>
